/* Progressive enhancement only: every product, configuration, film and gallery
   link has a native destination. No tracking or configuration-state mutation. */
(() => {
  'use strict';
  if (window.EFIslandShowroom) {
    window.EFIslandShowroom.init();
    return;
  }

  function preservePreview(root) {
    const current = new URL(window.location.href);
    const previewTheme = current.searchParams.get('preview_theme_id');
    const photoPreview = current.searchParams.get('efphoto');
    const validPhotoPreview = photoPreview === 'A' || photoPreview === 'B';
    if (previewTheme === null && !validPhotoPreview) return;
    const carryPreview = value => {
      if (!value || value.trim().startsWith('#')) return value;
      try {
        const target = new URL(value, current);
        const isJourney = target.pathname.startsWith('/products/') ||
          target.pathname.replace(/\/$/, '') === '/pages/outdoor-kitchen-islands';
        if (target.origin !== current.origin || !isJourney) return value;
        if (previewTheme !== null) target.searchParams.set('preview_theme_id', previewTheme);
        if (validPhotoPreview) target.searchParams.set('efphoto', photoPreview);
        return target.pathname + target.search + target.hash;
      } catch (_) {
        return value;
      }
    };
    root.querySelectorAll('a[href]').forEach(link => {
      const value = link.getAttribute('href');
      const updated = carryPreview(value);
      if (updated !== value) link.setAttribute('href', updated);
    });
    root.querySelectorAll('[data-efsh-configure]').forEach(link => {
      const value = link.dataset.efshConfigure;
      const updated = carryPreview(value);
      if (updated !== value) link.dataset.efshConfigure = updated;
    });
  }

  function initPhotoViews(root) {
    // Explicit preview links select the initial image set. There is no random
    // assignment, persistent visitor state, automatic experiment or tracking.
    const initial = new URL(window.location.href).searchParams.get('efphoto') === 'B' ? 'B' : 'A';
    root.querySelectorAll('[data-efsh-photo-a][data-efsh-photo-b]').forEach(image => {
      if (initial === 'B') {
        image.setAttribute('srcset', image.dataset.efshSrcsetB || '');
        image.src = image.dataset.efshPhotoB;
      }
    });
    const heroImage = root.querySelector('[data-efsh-hero-photo]');
    const choices = Array.from(root.querySelectorAll('[data-efsh-photo-option]'));
    if (!heroImage || !choices.length) return;
    const caption = root.querySelector('[data-efsh-photo-caption]');
    const initialAlt = heroImage.alt;
    const chooseView = choice => {
      const isProduct = choice.dataset.efshPhotoOption === 'product';
      heroImage.setAttribute('srcset', choice.dataset.efshPhotoSrcset || '');
      heroImage.src = choice.dataset.efshPhotoSrc;
      heroImage.classList.toggle('efsh-photo-is-product', isProduct || choice.dataset.efshPhotoFit === 'contain');
      heroImage.alt = choice.dataset.efshPhotoAlt || (isProduct ? initialAlt.replace('example design visualization', 'product photograph with example equipment and finish') : initialAlt);
      choices.forEach(item => item.setAttribute('aria-pressed', String(item === choice)));
      if (caption) caption.textContent = choice.dataset.efshViewCaption || (isProduct
        ? 'Product view. Appliances and selected options are additional. Choose your finish and equipment in the configurator.'
        : 'Example design. Appliances and selected options are additional. Choose your finish and equipment in the configurator.');
    };
    choices.forEach(choice => {
      choice.setAttribute('role', 'button');
      choice.setAttribute('aria-pressed', String(choice.dataset.efshPhotoOption === initial));
      choice.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        chooseView(choice);
      });
      choice.addEventListener('keydown', event => {
        if (event.key === ' ') {
          event.preventDefault();
          choice.click();
        }
      });
    });
  }

  function initShowroom(root) {
    if (root.dataset.efshReady === 'true') return;
    root.dataset.efshReady = 'true';
    preservePreview(root);
    initPhotoViews(root);

    // All quality panels remain readable if JavaScript is unavailable.
    const tablist = root.querySelector('[data-efsh-tabs]');
    if (tablist) {
      const tabs = Array.from(tablist.querySelectorAll('a[href^="#"]'));
      const panels = tabs.map(tab => root.querySelector(tab.getAttribute('href')));
      if (tabs.length && panels.every(Boolean)) {
        tablist.setAttribute('role', 'tablist');
        tabs.forEach((tab, index) => {
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', panels[index].id);
          panels[index].setAttribute('role', 'tabpanel');
          panels[index].setAttribute('aria-labelledby', tab.id);
          panels[index].tabIndex = 0;
        });
        const selectTab = index => {
          tabs.forEach((tab, i) => {
            tab.setAttribute('aria-selected', String(i === index));
            tab.tabIndex = i === index ? 0 : -1;
            panels[i].hidden = i !== index;
          });
        };
        const hashIndex = panels.findIndex(panel => `#${panel.id}` === window.location.hash);
        selectTab(hashIndex >= 0 ? hashIndex : 0);
        tabs.forEach((tab, index) => {
          tab.addEventListener('click', event => {
            event.preventDefault();
            selectTab(index);
          });
          tab.addEventListener('keydown', event => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            selectTab(next);
            tabs[next].focus();
          });
        });
      }
    }

    // Width comparison changes only the adjacent links, never a saved design.
    const comparison = root.querySelector('[data-efsh-compare]');
    if (comparison) {
      const rows = Array.from(comparison.querySelectorAll('[data-efsh-model]'));
      const title = comparison.querySelector('[data-efsh-compare-title]');
      const explore = comparison.querySelector('[data-efsh-compare-explore]');
      const configure = comparison.querySelector('[data-efsh-compare-configure]');
      rows.forEach(row => row.addEventListener('click', event => {
        // These are anchors until this enhancement is ready; each opens its model.
        event.preventDefault();
        rows.forEach(item => item.setAttribute('aria-pressed', String(item === row)));
        const name = row.dataset.efshName;
        const width = row.dataset.efshWidth;
        if (title) title.textContent = `${name} · ${width}″ wide. Choose your finish and equipment next.`;
        if (explore) {
          explore.href = row.href;
          explore.querySelector('span').textContent = `Explore ${name}`;
        }
        if (configure) {
          configure.href = row.dataset.efshConfigure;
          configure.querySelector('span').textContent = `Build ${width}″ in 3D`;
        }
      }));
      rows.forEach(row => {
        row.setAttribute('role', 'button');
        row.setAttribute('aria-pressed', row.dataset.efshSelected === 'true' ? 'true' : 'false');
        row.addEventListener('keydown', event => {
          if (event.key === ' ') {
            event.preventDefault();
            row.click();
          }
        });
      });
    }

    // Source is attached only by a deliberate play action. Native controls stay
    // available on pause; a replay control appears at the end of the film.
    root.querySelectorAll('[data-efsh-film]').forEach(figure => {
      const play = figure.querySelector('[data-efsh-film-play]');
      const stage = figure.querySelector('[data-efsh-film-stage]');
      if (!play || !stage) return;
      let video;
      let replay;
      const start = () => {
        if (!video) {
          video = document.createElement('video');
          video.className = 'efsh-film__video';
          video.controls = true;
          video.playsInline = true;
          video.tabIndex = 0;
          video.preload = 'none';
          video.poster = play.querySelector('img').currentSrc || play.querySelector('img').src;
          video.setAttribute('aria-label', figure.dataset.efshTitle || 'Outdoor kitchen film');
          video.src = play.href;
          stage.append(video);
          replay = document.createElement('button');
          replay.type = 'button';
          replay.className = 'efsh-film__replay';
          replay.textContent = 'Replay film';
          replay.hidden = true;
          replay.addEventListener('click', () => {
            video.currentTime = 0;
            start();
          });
          stage.append(replay);
          video.addEventListener('ended', () => { replay.hidden = false; });
          video.addEventListener('play', () => {
            replay.hidden = true;
            root.querySelectorAll('.efsh-film__video').forEach(other => {
              if (other !== video && !other.paused) other.pause();
            });
          });
          video.addEventListener('error', () => {
            video.hidden = true;
            replay.hidden = true;
            play.hidden = false;
            const label = figure.querySelector('[data-efsh-film-error]');
            if (label) label.hidden = false;
          });
        }
        play.hidden = true;
        video.hidden = false;
        replay.hidden = true;
        video.focus({ preventScroll: true });
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') {
          // Browser playback restrictions leave native controls ready to use.
          promise.catch(() => { video.controls = true; });
        }
      };
      play.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        start();
      });
    });

    // Native <dialog> supplies Escape, focus containment and background inertness.
    const dialog = root.querySelector('[data-efsh-lightbox]');
    if (dialog && typeof dialog.showModal === 'function') {
      const image = dialog.querySelector('[data-efsh-lightbox-image]');
      const caption = dialog.querySelector('[data-efsh-lightbox-caption]');
      const close = dialog.querySelector('[data-efsh-lightbox-close]');
      let opener;
      root.querySelectorAll('[data-efsh-zoom]').forEach(link => link.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        opener = link;
        image.src = link.href;
        image.alt = link.querySelector('img').alt;
        caption.textContent = link.dataset.efshCaption || image.alt;
        dialog.showModal();
        close.focus();
      }));
      close.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => { if (opener && opener.isConnected) opener.focus({ preventScroll: true }); });
    }

    // Model pages retain a concise route to their selected model. The bar is
    // absent until the hero leaves the viewport and yields to the help section.
    const sticky = root.querySelector('[data-efsh-sticky]');
    const hero = root.querySelector('[data-efsh-hero]');
    const help = root.querySelector('[data-efsh-help]');
    if (sticky && hero && 'IntersectionObserver' in window) {
      let heroPassed = false;
      let helpVisible = false;
      const sync = () => { sticky.hidden = !heroPassed || helpVisible || root.hidden; };
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === hero) heroPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          if (entry.target === help) helpVisible = entry.isIntersecting;
        });
        sync();
      }, { threshold: 0 });
      observer.observe(hero);
      if (help) observer.observe(help);
    }
  }

  const init = () => document.querySelectorAll('[data-efsh]').forEach(initShowroom);
  window.EFIslandShowroom = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
